import { Component } from '@angular/core';
import { FormGroup, FormBuilder, Validators, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { GlobalService } from '../../services/global.service';
import { DropdownService } from '../../services/dropdown.service';
import { ValidationService } from '../../services/validation.service';
import { StudentMappingService } from '../../services/student-mapping.service';
import { HeaderComponent } from '../../components/header/header.component';
import { SelectInputComponent } from '../../components/select-input/select-input.component';
import { TextInputComponent } from '../../components/text-input/text-input.component';

@Component({
  selector: 'app-student-mapping-form-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, HeaderComponent, SelectInputComponent, TextInputComponent],
  templateUrl: './student-mapping-form-page.component.html',
})
export class StudentMappingFormPageComponent {
  form: FormGroup;
  params: any = {};
  selected_item: any = {};
  isSubmitting = false;

  filtered_exams_for_form: any[] = [];
  filtered_subjects_for_exam: any[] = [];
  filtered_students_for_form: any[] = [];

  constructor(
    public gs: GlobalService,
    public ds: DropdownService,
    public vs: ValidationService,
    public sms: StudentMappingService,
    public fb: FormBuilder,
    public route: Router,
    public ar: ActivatedRoute,
  ) {
    this.form = this.fb.group({
      university_id: ['', [Validators.required]],
      academic_year_id: ['', [Validators.required]],
      exam_id: ['', [Validators.required]],
      subject_ids: [null, [Validators.required]],
      student_id: ['', [Validators.required]],
      roll_number: ['', [Validators.required, Validators.pattern(this.vs.roll_number_pattern), Validators.maxLength(this.vs.max_length_description)]],
      is_active: [true],
    });
  }

  async ngOnInit() {
    await this.ds.get_dropdown_list(['academic-year', 'university', 'exam', 'subject', 'student', 'assessment']);
    this.ar.queryParams.subscribe(async params => {
      this.params = { ...params };
      if (this.params.id) {
        const response = await this.sms.get(this.params.id);
        this.selected_item = response.data;
        this.form.patchValue({
          ...this.selected_item,
          university_id: this.selected_item.university_id || this.selected_item.University?.id,
          academic_year_id: this.selected_item.academic_year_id || this.selected_item.AcademicYear?.id,
          exam_id: this.selected_item.exam_id || this.selected_item.Exam?.id,
          student_id: this.selected_item.student_id || this.selected_item.User?.id,
        });
        this.filtered_exams_for_form = this.get_exams_for_filters(
          this.selected_item.university_id || this.selected_item.University?.id,
          this.selected_item.academic_year_id || this.selected_item.AcademicYear?.id
        );
        this.update_subjects_for_exam();
        // get_subjects_for_exam only lists subjects that currently have a matching Assessment —
        // a subject already mapped on this record might not (assessment since deleted/changed).
        // Without this, the multi-select silently drops any such value (SelectInputComponent
        // only keeps values it can find in its options list), so it renders empty on edit.
        (this.selected_item.Subjects || []).forEach((s: any) => {
          if (!this.filtered_subjects_for_exam.some((o: any) => o.value == s.id)) {
            this.filtered_subjects_for_exam.push({ title: s.name, value: s.id });
          }
        });
        this.update_students_for_university();
        setTimeout(() => {
          this.form.patchValue({
            subject_ids: this.selected_item.subject_ids || this.selected_item.Subjects?.map((s: any) => s.id),
          });
        });
      } else {
        if (!this.gs.access_scope.is_all) {
          const uni_id = this.gs.user_data?.university_id;
          if (uni_id) {
            this.form.patchValue({ university_id: uni_id });
            this.filtered_exams_for_form = this.get_exams_for_filters(uni_id, null);
            this.update_students_for_university();
          }
        }
      }
    });
  }

  get_exams_for_filters(university_id: any, academic_year_id: any): any[] {
    const exam_list = this.ds.dropdown_list?.exam_list;
    if (!exam_list) return [];
    return university_id
      ? exam_list.filter((e: any) => e.university_id == university_id)
      : exam_list;
  }

  get_subjects_for_exam(exam_id: any, university_id?: any, academic_year_id?: any): any[] {
    if (!exam_id || !this.ds.dropdown_list?.assessment_list) return [];
    const seen = new Set();
    return this.ds.dropdown_list.assessment_list
      .filter((a: any) =>
        a.exam_id == exam_id &&
        (!university_id || a.university_id == university_id) &&
        (!academic_year_id || a.academic_year_id == academic_year_id)
      )
      .reduce((acc: any[], a: any) => {
        const sid = a.subject?.id || a.subject_id;
        const name = a.subject?.name || a.subject?.title;
        if (sid && !seen.has(sid)) { seen.add(sid); acc.push({ title: name, value: sid }); }
        return acc;
      }, []);
  }

  update_subjects_for_exam() {
    this.filtered_subjects_for_exam = this.get_subjects_for_exam(
      this.form.get('exam_id')?.value,
      this.form.get('university_id')?.value,
      this.form.get('academic_year_id')?.value
    );
  }

  update_students_for_university() {
    const university_id = this.form.get('university_id')?.value;
    const all = this.ds.dropdown_list?.student_name_list || [];
    this.filtered_students_for_form = university_id
      ? all.filter((s: any) => s.university_id == university_id)
      : all;
  }

  onUniversityOrYearChange() {
    this.form.patchValue({ exam_id: '', subject_ids: null, student_id: '' });
    this.filtered_subjects_for_exam = [];
    this.filtered_exams_for_form = this.get_exams_for_filters(
      this.form.get('university_id')?.value,
      this.form.get('academic_year_id')?.value
    );
    this.update_students_for_university();
  }

  onExamChange() {
    this.form.get('subject_ids')?.reset();
    this.update_subjects_for_exam();
  }

  async submit() {
    if (this.form.invalid) {
      this.gs.toastr_shows_function('Please fill all required fields correctly.', 'Error', 'error');
      this.form.markAllAsTouched();
      return;
    }
    try {
      this.isSubmitting = true;
      const form_value = { ...this.form.value, is_active: true };
      if (this.selected_item?.id) {
        await this.sms.update(this.selected_item.id, form_value);
        this.gs.toastr_shows_function('Student Mapping Updated Successfully', 'Success', 'success');
      } else {
        await this.sms.add(form_value);
        this.gs.toastr_shows_function('Student Mapped Successfully', 'Success', 'success');
      }
      this.ds.invalidate_dropdown_cache('student');
      this.route.navigate(['/student-mapping/list']);
    } catch (error: any) {
      this.gs.toastr_shows_function(error?.error?.message, 'Error', 'error');
    } finally {
      this.isSubmitting = false;
    }
  }

  get page_label() {
    return this.selected_item?.id ? 'Update Student Mapping' : 'Add Student Mapping';
  }
}
